import Vue from 'vue';
import App from './App.vue';
import router from './router';
import store from '@/core/services/store';
import ApiService from '@/core/services/api.service';
import { registerPermissionContext } from '@/core/services/permission.service';
import MgrService from '@/core/services/security.service';
import BroadcastService from '@/core/services/broadcast.service';

// import MockService from '@/core/mock/mock.service';
// import { VERIFY_AUTH } from "@/core/services/store/auth.module";

Vue.config.productionTip = false;

// Global 3rd party plugins
import 'popper.js';
import 'tooltip.js';
import PerfectScrollbar from 'perfect-scrollbar';
window.PerfectScrollbar = PerfectScrollbar;
import ClipboardJS from 'clipboard';
window.ClipboardJS = ClipboardJS;

// Vue 3rd party plugins
import i18n from '@/core/plugins/vue-i18n';
import vuetify from '@/core/plugins/vuetify';
import '@/core/plugins/portal-vue';
import '@/core/plugins/bootstrap-vue';
import '@/core/plugins/perfect-scrollbar';
import '@/core/plugins/highlight-js';
import '@/core/plugins/inline-svg';
import '@/core/plugins/apexcharts';
import '@/core/plugins/metronic';
import '@mdi/font/css/materialdesignicons.css';
import '@/core/plugins/vue-swal';
import '@/core/plugins/vue-toastr';
import '@/core/plugins/vee-validate';
import 'vue-select/dist/vue-select.css';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
import '@/core/plugins/moment';
import '@/core/plugins/vue-mask';
import '@/core/plugins/vue-uuid';
import '@/core/plugins/mixins';
// Global Components
import '@/components/global';
import '@/core/mixins';

// Custom Directive
import '@/directive';

// path/to/main.(js|ts)


import Vueform from '@vueform/vueform'
import vueformConfig from './../vueform.config'


// Import required styles


Vue.prototype.$api = new ApiService();
Vue.prototype.$mgr = new MgrService();
Vue.prototype.$broadcast = new BroadcastService();
// // Remove this to disable mock API
// MockService.init();
try {
  Vue.use(Vueform, vueformConfig);
} catch (error) {
  console.warn('Vueform plugin is not compatible in current runtime:', error);
}

const GA_MEASUREMENT_ID = 'G-Q3CEX9ERMR';

function initGoogleAnalytics() {
  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  if (!window.gtag) {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }

  if (!window.__gaScriptInjected) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
    window.__gaScriptInjected = true;
  }

  if (!window.__gaInitialized) {
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: false,
      debug_mode: true,
    });
    window.__gaInitialized = true;
  }
}

function pickFirstValue(source, keys) {
  for (const key of keys) {
    if (source && source[key] !== undefined && source[key] !== null) {
      return source[key];
    }
  }
  return null;
}

function getOidcUserFromStorage() {
  try {
    const key = Object.keys(localStorage).find((item) =>
      item.startsWith('oidc.user:'),
    );
    if (!key) return null;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
}

const EMAIL_LIKE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isEmailString(value) {
  return typeof value === 'string' && EMAIL_LIKE.test(value.trim());
}

function firstEmailLike(...candidates) {
  for (const c of candidates) {
    if (c == null) continue;
    const s = typeof c === 'string' ? c.trim() : String(c).trim();
    if (EMAIL_LIKE.test(s)) return s;
  }
  return null;
}

function resolveUserEmail(storeUser, oidcProfile) {
  const nested = [
    storeUser?.data,
    storeUser?.user,
    storeUser?.profile,
    storeUser?.account,
  ].filter(Boolean);

  for (const obj of nested) {
    const e = firstEmailLike(
      obj.email,
      obj.mail,
      obj.userEmail,
      obj.userName,
      obj.username,
    );
    if (e) return e;
  }

  const fromStore = firstEmailLike(
    pickFirstValue(storeUser, [
      'email',
      'mail',
      'userEmail',
      'userName',
      'username',
    ]),
  );
  if (fromStore) return fromStore;

  const fromOidc = firstEmailLike(
    pickFirstValue(oidcProfile, [
      'email',
      'upn',
      'unique_name',
      'preferred_username',
    ]),
  );
  if (fromOidc) return fromOidc;

  const username = pickFirstValue(storeUser, ['userName', 'username']);
  if (isEmailString(username)) return username.trim();

  return null;
}

function setGaUserContext() {
  if (!window.gtag) {
    return;
  }

  const storeUser = store?.state?.auth?.user || {};
  const oidcUser = getOidcUserFromStorage() || {};
  const oidcProfile = oidcUser.profile || {};

  const userId =
    pickFirstValue(storeUser, ['id', 'userId', 'accountId', 'sub']) ||
    pickFirstValue(oidcProfile, ['sub', 'id']);
  const userEmail = resolveUserEmail(storeUser, oidcProfile);
  const userName =
    pickFirstValue(storeUser, ['fullName', 'name', 'displayName']) ||
    pickFirstValue(oidcProfile, ['name', 'given_name']);

  const userProps = {
    user_name: userName || 'anonymous',
  };
  if (userEmail) {
    userProps.email = userEmail;
    userProps.user_email = userEmail;
  }

  window.gtag('set', 'user_properties', userProps);

  window.gtag('config', GA_MEASUREMENT_ID, {
    user_id: userId ? String(userId) : undefined,
    send_page_view: false,
    debug_mode: true,
  });

  window.gtag('event', 'user_context_set', {
    has_user_id: Boolean(userId),
    has_user_email: Boolean(userEmail),
    debug_mode: true,
  });
}

function trackPageView(to) {
  if (!window.gtag) {
    return;
  }

  window.gtag('event', 'page_view', {
    page_title: document.title || to.name || 'unknown_page',
    page_path: to.path,
    page_location: window.location.href,
    route_name: to.name || '',
    debug_mode: true,
  });
}

function setupButtonClickTracking() {
  if (window.__gaButtonTrackingBound) {
    return;
  }

  const CLICK_THROTTLE_MS = 350;
  let lastClickedKey = null;
  let lastClickedAt = 0;

  const resolveClickableTarget = (event) => {
    if (!event.target || !event.target.closest) return null;
    return event.target.closest(
      [
        'button',
        'a.btn',
        '[role="button"]',
        '.btn',
        '.navi-link',
        '.cursor-pointer',
      ].join(','),
    );
  };

  const getElementLabel = (target) => {
    return (
      target.getAttribute('data-ga-label') ||
      target.getAttribute('aria-label') ||
      target.getAttribute('title') ||
      (target.innerText || target.textContent || '').trim().slice(0, 120) ||
      'unknown_button'
    );
  };

  document.addEventListener('click', (event) => {
    if (!window.gtag) {
      return;
    }

    const target = resolveClickableTarget(event);
    if (!target) {
      return;
    }
    if (target.getAttribute('data-ga-ignore') === 'true') {
      return;
    }

    const buttonText = getElementLabel(target);
    const clickKey = [
      target.tagName,
      target.id || '',
      target.className || '',
      buttonText,
      window.location.pathname,
    ].join('|');
    const now = Date.now();
    if (lastClickedKey === clickKey && now - lastClickedAt < CLICK_THROTTLE_MS) {
      return;
    }
    lastClickedKey = clickKey;
    lastClickedAt = now;

    window.gtag('event', 'button_click', {
      button_text: buttonText || 'unknown_button',
      button_id: target.id || '',
      button_class: target.className || '',
      button_tag: target.tagName.toLowerCase(),
      page_path: window.location.pathname,
      debug_mode: true,
    });
  }, true);

  window.__gaButtonTrackingBound = true;
}

initGoogleAnalytics();
setupButtonClickTracking();
router.afterEach((to) => {
  setGaUserContext();
  trackPageView(to);
});

registerPermissionContext(router, store);

new Vue({
  router,
  store,
  i18n,
  vuetify,
  render: (h) => h(App),
}).$mount('#app');
